/**
 * Realtime WS message shapes (T-035) — docs/10-design/10-notifications.md §6 · tasks/W3-CONTRACTS.md §7
 * server→client บนห้องการ์ด (`RoomMessage`) และ hub ของผู้ใช้ (`HubMessage`) · client→server (`ClientMessage`)
 * ⛔ ห้ามส่งข้อมูลการ์ดผ่าน WS — แค่ "มีอะไรเปลี่ยน" ให้ client refetch REST (W3 §7)
 */
import { z } from "zod";
import { Notification } from "./notifications.ts"; // [T-031]

export const RoomRole = z.enum(["holder", "member", "customer", "guest", "viewer"]);
export type RoomRole = z.infer<typeof RoomRole>;

export const PresenceState = z.enum(["viewing", "typing_reply", "typing_note"]);
export type PresenceState = z.infer<typeof PresenceState>;

export const PresenceViewer = z.object({
  userId: z.string(),
  display: z.string(),
  role: RoomRole,
  state: PresenceState,
});
export type PresenceViewer = z.infer<typeof PresenceViewer>;

/** server → client บน `/ws/tickets/:id` */
export const RoomMessage = z.discriminatedUnion("type", [
  z.object({
    seq: z.number().int(),
    type: z.literal("ticket_event"),
    event_type: z.string(),
    ticket_id: z.string(),
    at: z.iso.datetime(),
    data: z
      .object({
        message_id: z.string().optional(),
        seq: z.number().int().optional(),
        kind: z.string().optional(),
        to: z.array(z.string()).optional(),
        from: z.string().optional(),
        user_id: z.string().optional(),
      })
      .optional(),
  }),
  z.object({
    type: z.literal("presence"),
    viewers: z.array(PresenceViewer),
  }),
  z.object({
    type: z.literal("typing"),
    user_id: z.string(),
    state: PresenceState,
  }),
]);
export type RoomMessage = z.infer<typeof RoomMessage>;

/** server → client บน `/ws/me` */
export const HubMessage = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("notification"),
    notification: Notification,
  }),
  z.object({
    type: z.literal("badge"),
    unread_count: z.number().int(),
  }),
  z.object({
    type: z.literal("ticket_updated"),
    ticket_id: z.string(),
    event_type: z.string(),
  }),
]);
export type HubMessage = z.infer<typeof HubMessage>;

/** client → server (ping/pong ผ่าน WebSocket auto-response ของ DO ไม่ใช่ JSON message นี้) */
export const ClientMessage = z.object({
  type: z.literal("presence"),
  state: PresenceState,
});
export type ClientMessage = z.infer<typeof ClientMessage>;
